import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import AmexImage from '../../AmexMarketingUtils/AmexImage';
import { BREAKPOINTS } from '../../AmexMarketingUtils/constants';
import ResyRewardsAppStoreButton from '../ResyRewardsAppStoreButton';
import ResyRewardsBackgroundVideo from '../ResyRewardsBackgroundVideo';
import './ResyRewardsBanner.scss';

const ResyRewardsBanner = ({
  appStoreUrl,
  bannerConfig,
  eventAnalytics,
  isClosing,
  matchMedia,
  urlPrefix,
  videoUrlPrefix,
}) => {
  const {
    eyebrow,
    title,
    copy,
    images,
    image_alt: imageAlt = '',
    video,
  } = bannerConfig || {};

  if (!title) return null;

  const TitleTag = isClosing ? 'h2' : 'h1';
  const posterName = matchMedia.md ? images?.desktop : images?.mobile;

  const renderMedia = () => {
    if (video && videoUrlPrefix) {
      return (
        <ResyRewardsBackgroundVideo
          eventAnalytics={eventAnalytics}
          matchMedia={matchMedia}
          posterUrl={posterName ? `${urlPrefix}${posterName}` : ''}
          videoConfig={video}
          videoUrlPrefix={videoUrlPrefix || ''}
        />
      );
    }
    return (
      <AmexImage
        images={images}
        imageAlt={imageAlt}
        mediaBreakpoint={BREAKPOINTS.LARGE}
        matchMedia={matchMedia}
        urlPrefix={urlPrefix}
      />
    );
  };

  return (
    <section
      className={cx('ResyRewardsBanner', {
        'ResyRewardsBanner--hero': !isClosing,
        'ResyRewardsBanner--closing': isClosing,
      })}
    >
      <div className="ResyRewardsBanner__card">
        {(video || images) && (
          <div className="ResyRewardsBanner__media">
            {renderMedia()}
          </div>
        )}
        <div className="container ResyRewardsBanner__container">
          <div className="ResyRewardsBanner__heading">
            {eyebrow && <p className="ResyRewardsBanner__eyebrow">{eyebrow}</p>}
            <TitleTag
              className="ResyRewardsBanner__title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizeString(title) }}
            />
          </div>
          <div className="ResyRewardsBanner__body-track">
            <div className="ResyRewardsBanner__body">
              {copy && (
                <p
                  className="ResyRewardsBanner__copy"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: sanitizeString(copy) }}
                />
              )}
              <ResyRewardsAppStoreButton
                appStoreUrl={appStoreUrl}
                eventAnalytics={eventAnalytics}
                sectionName={isClosing ? 'Closing' : 'Hero'}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

ResyRewardsBanner.propTypes = {
  appStoreUrl: PropTypes.string,
  bannerConfig: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    copy: PropTypes.string,
    image_alt: PropTypes.string,
    images: PropTypes.shape({
      desktop: PropTypes.string,
      mobile: PropTypes.string,
    }),
    video: PropTypes.shape({
      desktop: PropTypes.string,
      mobile: PropTypes.string,
      play_label: PropTypes.string,
      pause_label: PropTypes.string,
    }),
  }),
  eventAnalytics: PropTypes.func,
  isClosing: PropTypes.bool,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  urlPrefix: PropTypes.string.isRequired,
  videoUrlPrefix: PropTypes.string,
};

ResyRewardsBanner.defaultProps = {
  appStoreUrl: '',
  bannerConfig: null,
  eventAnalytics: null,
  isClosing: false,
  videoUrlPrefix: '',
};

export default ResyRewardsBanner;
